import { QueryErrorResetBoundary } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { Component } from 'react'

type FallbackProps = {
  error: unknown
  /** clears the error and loads the failed queries again */
  retry: () => void
}

type BoundaryProps = {
  children: ReactNode
  fallback: (props: FallbackProps) => ReactNode
  /** a change (e.g. new filters) clears the error and renders again */
  resetKey: string
  onReset: () => void
}

type BoundaryState = { error: unknown; failed: boolean }

class ErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  override state: BoundaryState = { error: undefined, failed: false }

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error, failed: true }
  }

  override componentDidUpdate(previous: BoundaryProps) {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.reset()
    }
  }

  reset = () => {
    this.props.onReset()
    this.setState({ error: undefined, failed: false })
  }

  override render() {
    return this.state.failed
      ? this.props.fallback({ error: this.state.error, retry: this.reset })
      : this.props.children
  }
}

/**
 * Catches what a suspense query (`useSuspenseQuery`) below it throws and
 * shows `fallback` instead, so one failed section leaves the rest of the
 * page. Retry, and a new `resetKey`, reset TanStack Query's error state too,
 * so the query loads again instead of throwing its cached error.
 */
export function QueryErrorBoundary({
  children,
  fallback,
  resetKey,
}: {
  children: ReactNode
  fallback: (props: FallbackProps) => ReactNode
  resetKey: string
}) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          fallback={fallback}
          resetKey={resetKey}
          onReset={reset}
        >
          {children}
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  )
}
